/**
 * /whatsapp/agendamentos — Mensagens em status SCHEDULED.
 *
 * Permite ao gestor/atendente cancelar antes do cron disparar.
 * Polling 30s (status muda lento — sem urgencia).
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw, FiX } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import useWhatsappPolling from "../../../hooks/useWhatsappPolling";
import {
  cancelarMensagem,
  listarMensagensAgendadas,
  STATUS_MENSAGEM_LABEL,
  STATUS_MENSAGEM_TONE,
} from "../../../services/whatsapp";
import { PageContainer } from "../../../styles/shared";
import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  FilterBar,
  InlineHint,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";

export default function AgendamentosLista() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("SCHEDULED");

  const carregar = useCallback(async () => {
    setError(null);
    try {
      const payload = await listarMensagensAgendadas(request, {
        status: filtroStatus || "SCHEDULED",
        pageSize: 200,
      });
      const lista = Array.isArray(payload) ? payload : payload?.results || [];
      setItems(lista);
    } catch (err) {
      setError(err?.message || "Erro ao carregar agendamentos.");
    }
  }, [request, filtroStatus]);

  useWhatsappPolling(carregar, 30000, [carregar]);

  const contagens = useMemo(() => {
    const agora = Date.now();
    const proximas24h = items.filter((m) => {
      if (m.status !== "SCHEDULED" || !m.scheduled_at) return false;
      const t = new Date(m.scheduled_at).getTime();
      return t > agora && t - agora <= 86400000;
    }).length;
    const atrasadas = items.filter((m) => {
      if (m.status !== "SCHEDULED" || !m.scheduled_at) return false;
      return new Date(m.scheduled_at).getTime() < agora;
    }).length;
    return {
      total: items.length,
      agendadas: items.filter((m) => m.status === "SCHEDULED").length,
      proximas24h,
      atrasadas,
    };
  }, [items]);

  const kpis = useMemo(
    () => [
      { label: "Total exibido", value: String(contagens.total) },
      { label: "Agendadas", value: String(contagens.agendadas), tone: "warning" },
      { label: "Próximas 24h", value: String(contagens.proximas24h), tone: "info" },
      { label: "Atrasadas", value: String(contagens.atrasadas), tone: "danger" },
    ],
    [contagens],
  );

  const handleCancelar = async (id) => {
    if (!window.confirm("Cancelar esta mensagem agendada? Operação irreversível.")) {
      return;
    }
    await cancelarMensagem(request, id);
    await carregar();
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Mensagens agendadas"
        subtitle="Worker cron dispara as agendadas (~1 min de granularidade)"
        secondaryActions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw />,
            onClick: carregar,
          },
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Lista</SectionTitle>
        <FilterBar>
          <Select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            style={{ maxWidth: 180 }}
          >
            <option value="SCHEDULED">Agendadas</option>
            <option value="SENT">Enviadas</option>
            <option value="CANCELED">Canceladas</option>
            <option value="FAILED">Falhadas</option>
          </Select>
        </FilterBar>

        {error && <ErrorMsg>{error}</ErrorMsg>}
        {!error && items.length === 0 && (
          <EmptyMsg>Nenhuma mensagem nesse filtro.</EmptyMsg>
        )}

        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Conversa</th>
                  <th>Texto</th>
                  <th>Autor</th>
                  <th>Status</th>
                  <th style={{ width: 100 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td>{fmtData(m.scheduled_at) || "—"}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/whatsapp/inbox/${m.conversation || m.conversation_id}`)
                        }
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#1d4ed8",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: "inherit",
                        }}
                      >
                        {m.contact_nome || m.contact_wa_number || "—"}
                      </button>
                    </td>
                    <td
                      style={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={m.raw_text}
                    >
                      {m.raw_text}
                    </td>
                    <td>{m.erp_user_nome || <InlineHint>—</InlineHint>}</td>
                    <td>
                      <Pill $tone={STATUS_MENSAGEM_TONE[m.status] || "neutral"}>
                        {STATUS_MENSAGEM_LABEL[m.status] || m.status}
                      </Pill>
                    </td>
                    <td>
                      {m.status === "SCHEDULED" && (
                        <AsyncButton
                          variant="danger"
                          onClick={() => handleCancelar(m.id)}
                          successMessage="Mensagem cancelada"
                          errorMessage="Falha ao cancelar"
                        >
                          <FiX /> Cancelar
                        </AsyncButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
    </PageContainer>
  );
}
